import React, { useState, useEffect } from 'react'
import { Box, Button, Grid, Alert, Typography, Paper } from '@mui/material'
import { isEqual } from 'lodash'
import { TextEditor } from 'components/TextEditor/TextEditor'
import { useGlobalNavigationBlock } from '../useGlobalNavigationBlock'
import GenericMultiLineTextField from 'components/UserFields/GenericMultiLineTextField'
import { getChangedFields } from '../helpers'

const AboutYouSection = ({ formData, onSave }) => {
  const [localFormData, setLocalFormData] = useState({
    ...formData,
  })
  const [initialData, setInitialData] = useState(formData)
  const [hasChanges, setHasChanges] = useState(false)
  useGlobalNavigationBlock(hasChanges)

  useEffect(() => {
    setLocalFormData({
      ...formData,
    })
    setInitialData(formData)
    setHasChanges(false)
  }, [formData])

  const handleInputChange = event => {
    const { name, value } = event.target
    setLocalFormData(prev => ({ ...prev, [name]: value }))
    setHasChanges(!isEqual(localFormData, initialData))
  }

  const handleEditorChange = content => {
    setLocalFormData(prev => ({ ...prev, about: content.about, aboutRaw: content.aboutRaw }))
    setHasChanges(!isEqual(localFormData, initialData))
  }

  const handleSave = () => {
    const changedFields = getChangedFields(localFormData, initialData)

    if (Object.keys(changedFields).length > 0) {
      onSave({
        ...changedFields,
        id: formData.id,
      })
      setInitialData(localFormData)
      setHasChanges(false)
    }
  }

  return (
    <Box>
      {hasChanges && (
        <Alert 
          severity='info' 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: 'info.main'
            }
          }}
        >
          You have unsaved changes in this section. Please save before navigating away.
        </Alert>
      )}

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default'
            }}
          >
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                    About You
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Share your journey, skills, and values that set you apart. Highlight accomplishments and experiences. Use paragraphs or bullet points to make it recruiter-friendly!
                  </Typography>
                </Box>
                <TextEditor
                  label=''
                  rows={8}
                  initialValue={localFormData.about || ''}
                  placeholder='Share your journey, skills, and values that set you apart...'
                  onChange={handleEditorChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.default'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                    Ideal Job Description
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Describe your ideal role, work environment, location, and career goals. Be specific to help recruiters find the best match for you.
                  </Typography>
                </Box>
                <GenericMultiLineTextField
                  name='aboutJob'
                  label=''
                  placeholder='Describe your ideal role, work environment, location, and career goals...'
                  value={localFormData.aboutJob}
                  handleChange={handleInputChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.default'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Button
        variant='contained'
        color='primary'
        onClick={handleSave}
        disabled={!hasChanges}
        sx={{ 
          mt: 4,
          px: 4,
          py: 1.5,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '0.9375rem',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none'
          }
        }}
      >
        Save Changes
      </Button>
    </Box>
  )
}

export default AboutYouSection
